/**
 * 64. Work Centers — resource detail; capacity; calendar; efficiency.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { CreateRecordButton } from "@/components/CreateRecordButton";

interface WorkCenterRow {
  id: string;
  code: string;
  name: string;
  capacityPerHour: string | null;
  utilization: string;
  efficiency: string;
  status: "Active" | "Inactive";
}

export function WorkCentersPage() {
  const { data, isLoading } = useApiList<WorkCenterRow>(
    ["manufacturing", "work-centers"],
    "/manufacturing/work-centers",
  );

  return (
    <div>
      <PageHeader
        title="Work Centers"
        subtitle="Production resource detail: capacity, calendar, efficiency."
        actions={
          <CreateRecordButton
            label="New work center"
            path="/work-centers"
            invalidate={["manufacturing", "work-centers"]}
            fields={[
              { name: "code", label: "Code", required: true },
              { name: "name", label: "Name", required: true },
              {
                name: "capacityPerHour",
                label: "Capacity per hour",
                type: "number",
              },
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Work centers (${data.total})`}>
          <DataTable<WorkCenterRow>
            rows={data.items}
            columns={[
              {
                key: "code",
                label: "Code",
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              { key: "name", label: "Name" },
              {
                key: "capacityPerHour",
                label: "Capacity / hour",
                render: (r) => r.capacityPerHour ?? "—",
              },
              {
                key: "utilization",
                label: "Utilization",
                render: (r) => (
                  <Badge
                    tone={
                      Number(r.utilization) > 0.9
                        ? "warning"
                        : Number(r.utilization) > 0.75
                          ? "primary"
                          : "success"
                    }
                  >
                    {r.utilization}
                  </Badge>
                ),
              },
              { key: "efficiency", label: "Efficiency" },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
